import React, { useEffect, useId, useRef, useState } from 'react';
import './Onboarding.css';

/**
 * A small "?" next to a field or heading that explains it in a sentence or two.
 * Opens on hover, keyboard focus or tap; Escape or a tap elsewhere closes it.
 * Keep it outside <label> elements so it doesn't change the field's accessible name.
 */
export default function HelpTip({ label, children, align = 'start' }) {
    const [open, setOpen] = useState(false);
    const [pinned, setPinned] = useState(false);
    const wrapRef = useRef(null);
    const tipId = useId();

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (event) => { if (event.key === 'Escape') { setOpen(false); setPinned(false); } };
        const onPointer = (event) => {
            if (!wrapRef.current?.contains(event.target)) { setOpen(false); setPinned(false); }
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('pointerdown', onPointer);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('pointerdown', onPointer);
        };
    }, [open]);

    return (
        <span
            ref={wrapRef}
            className={`help-tip align-${align}`}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => { if (!pinned) setOpen(false); }}
        >
            <button
                type="button"
                className="help-tip-trigger"
                aria-label={label}
                aria-expanded={open}
                aria-describedby={open ? tipId : undefined}
                onFocus={() => setOpen(true)}
                onBlur={() => { if (!pinned) setOpen(false); }}
                onClick={(event) => {
                    event.preventDefault();
                    // A tap pins the tip open (touch screens have no hover); a second tap closes it.
                    setPinned(!pinned);
                    setOpen(!pinned);
                }}
            >
                ?
            </button>
            {open && <span id={tipId} role="tooltip" className="help-tip-bubble">{children}</span>}
        </span>
    );
}
